/* eslint-disable @typescript-eslint/no-explicit-any */
import  { useState } from 'react';
import {
TrendingUp, Phone, Users, Calendar, Bell, BookOpen, Brain,
  ChevronRight, X, Activity, Clock, Target,  MessageSquare,
  Mail, Video, CheckCircle, XCircle, Lightbulb, Eye, Filter
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

type TabType = 'contact_centre' | 'patients' | 'reminders' | 'appointments' | 'education' | 'users';
type DrillDownType = 'call_volume' | 'call_duration' | 'intent_breakdown' | 'urgency_levels' |
  'patient_growth' | 'patient_demographics' | 'reminder_delivery' | 'reminder_effectiveness' |
  'appointment_status' | 'appointment_types' | 'education_engagement' | 'content_performance' | null;

interface FilterState {
  dateRange: 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all';
  channel?: 'call' | 'sms' | 'email' | 'all';
  status?: string;
  type?: string;
}

export default function Analytics() {
  const { state } = useAppContext();
  const { calls, patients, appointments, reminders } = state;

  const [activeTab, setActiveTab] = useState<TabType>('contact_centre');
  const [drillDownData, setDrillDownData] = useState<DrillDownType>(null);
  const [filters, setFilters] = useState<FilterState>({
    dateRange: 'all',
    channel: 'all'
  });

  const closeDrillDown = () => setDrillDownData(null);

  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Insights & Reports</h1>
            <p className="text-blue-100">AI-powered analytics with actionable drill-down insights</p>
          </div>
          <div className="bg-white bg-opacity-20 rounded-lg p-4 text-center">
            <Brain className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm font-semibold">AI Engine Active</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            <TabButton
              active={activeTab === 'contact_centre'}
              onClick={() => setActiveTab('contact_centre')}
              icon={<Phone className="w-5 h-5" />}
              label="Contact Centre"
            />
            <TabButton
              active={activeTab === 'patients'}
              onClick={() => setActiveTab('patients')}
              icon={<Users className="w-5 h-5" />}
              label="Patients"
            />
            <TabButton
              active={activeTab === 'reminders'}
              onClick={() => setActiveTab('reminders')}
              icon={<Bell className="w-5 h-5" />}
              label="Reminders"
            />
            <TabButton
              active={activeTab === 'appointments'}
              onClick={() => setActiveTab('appointments')}
              icon={<Calendar className="w-5 h-5" />}
              label="Appointments"
            />
            <TabButton
              active={activeTab === 'education'}
              onClick={() => setActiveTab('education')}
              icon={<BookOpen className="w-5 h-5" />}
              label="Education"
            />
            <TabButton
              active={activeTab === 'users'}
              onClick={() => setActiveTab('users')}
              icon={<Activity className="w-5 h-5" />}
              label="New Users"
            />
          </nav>
        </div>

        <div className="p-6">
          {/* Filters Section */}
          <FilterBar
            activeTab={activeTab}
            filters={filters}
            onFilterChange={updateFilter}
          />

          {activeTab === 'contact_centre' && (
            <ContactCentreAnalytics calls={calls} reminders={reminders} filters={filters} onDrillDown={setDrillDownData} />
          )}
          {activeTab === 'patients' && (
            <PatientsAnalytics patients={patients} filters={filters} onDrillDown={setDrillDownData} />
          )}
          {activeTab === 'reminders' && (
            <RemindersAnalytics reminders={reminders} filters={filters} onDrillDown={setDrillDownData} />
          )}
          {activeTab === 'appointments' && (
            <AppointmentsAnalytics appointments={appointments} filters={filters} onDrillDown={setDrillDownData} />
          )}
          {activeTab === 'education' && (
            <EducationAnalytics filters={filters} onDrillDown={setDrillDownData} />
          )}
          {activeTab === 'users' && (
            <NewUsersAnalytics patients={patients} filters={filters} onDrillDown={setDrillDownData} />
          )}
        </div>
      </div>

      {/* Drill Down Modal */}
      {drillDownData && (
        <DrillDownModal type={drillDownData} onClose={closeDrillDown} calls={calls} patients={patients} reminders={reminders} appointments={appointments} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, icon, label }: any) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
        active
          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function FilterBar({ activeTab, filters, onFilterChange }: any) {
  const dateRanges = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' }
  ];

  const getTabSpecificFilters = () => {
    switch (activeTab) {
      case 'contact_centre':
        return [
          { key: 'channel', options: [
            { value: 'all', label: 'All Channels' },
            { value: 'call', label: 'Calls' },
            { value: 'sms', label: 'SMS' },
            { value: 'email', label: 'Email' }
          ]}
        ];
      case 'appointments':
        return [
          { key: 'status', options: [
            { value: 'all', label: 'All Status' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'completed', label: 'Completed' },
            { value: 'no-show', label: 'No-Show' }
          ]},
          { key: 'type', options: [
            { value: 'all', label: 'All Types' },
            { value: 'cleaning', label: 'Cleaning' },
            { value: 'consultation', label: 'Consultation' },
            { value: 'emergency', label: 'Emergency' },
            { value: 'procedure', label: 'Procedure' }
          ]}
        ];
      case 'reminders':
        return [
          { key: 'channel', options: [
            { value: 'all', label: 'All Channels' },
            { value: 'sms', label: 'SMS' },
            { value: 'email', label: 'Email' },
            { value: 'call', label: 'Call' }
          ]},
          { key: 'status', options: [
            { value: 'all', label: 'All Status' },
            { value: 'sent', label: 'Sent' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'failed', label: 'Failed' }
          ]}
        ];
      case 'education':
        return [
          { key: 'type', options: [
            { value: 'all', label: 'All Types' },
            { value: 'video', label: 'Video' },
            { value: 'article', label: 'Article' },
            { value: 'infographic', label: 'Infographic' }
          ]}
        ];
      default:
        return [];
    }
  };

  return (
    <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="flex items-center space-x-2 mb-3">
        <Filter className="w-5 h-5 text-gray-600" />
        <h3 className="font-semibold text-gray-900">Filters</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {dateRanges.map((range) => (
          <button
            key={range.value}
            onClick={() => onFilterChange('dateRange', range.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filters.dateRange === range.value
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
            }`}
          >
            {range.label}
          </button>
        ))}

        {getTabSpecificFilters().map((filterGroup) => (
          <div key={filterGroup.key} className="flex items-center space-x-2 ml-4">
            <span className="text-sm text-gray-600">|</span>
            {filterGroup.options.map((option) => (
              <button
                key={option.value}
                onClick={() => onFilterChange(filterGroup.key, option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  (filters as any)[filterGroup.key] === option.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactCentreAnalytics({ 
  calls,
   reminders, 
  //  filters, 
   onDrillDown }: any) {
  const totalCalls = calls.length;
  const totalSMS = reminders.filter((r: any) => r.type === 'sms').length;
  const totalEmails = reminders.filter((r: any) => r.type === 'email').length;
  const totalContacts = totalCalls + totalSMS + totalEmails;

  // const avgDuration = calls.reduce((sum: number, c: any) => sum + (c.duration || 0), 0) / (calls.length || 1);
  // const emergencyCalls = calls.filter((c: any) => c.urgencyLevel === 'emergency').length;
  // const inboundCalls = calls.filter((c: any) => c.type === 'inbound').length;

  const channelData = [
    { channel: 'Calls', count: totalCalls, color: 'blue', icon: <Phone className="w-5 h-5" /> },
    { channel: 'SMS', count: totalSMS, color: 'green', icon: <MessageSquare className="w-5 h-5" /> },
    { channel: 'Email', count: totalEmails, color: 'purple', icon: <Mail className="w-5 h-5" /> }
  ];

  const maxChannelCount = Math.max(totalCalls, totalSMS, totalEmails);

  const intentData = [
    { intent: 'Emergency', count: calls.filter((c: any) => c.purpose === 'emergency').length, color: 'red' },
    { intent: 'Appointment', count: calls.filter((c: any) => c.purpose === 'appointment').length, color: 'blue' },
    { intent: 'Follow-up', count: calls.filter((c: any) => c.purpose === 'follow-up').length, color: 'green' },
    { intent: 'Inquiry', count: calls.filter((c: any) => c.purpose === 'inquiry').length, color: 'yellow' }
  ];

  const aiRecommendations = [
    { insight: 'SMS engagement 40% higher than calls', action: 'Prioritize SMS for routine communications', priority: 'high' },
    { insight: 'Peak contact volume between 2-4 PM', action: 'Consider adding staff during peak hours', priority: 'high' },
    { insight: 'Email open rate at 68%', action: 'Test subject line optimization', priority: 'medium' }
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Contacts"
          value={totalContacts.toString()}
          subtitle="All channels"
          icon={<Activity className="w-6 h-6" />}
          color="blue"
          onClick={() => onDrillDown('call_volume')}
        />
        <MetricCard
          title="Calls"
          value={totalCalls.toString()}
          subtitle={`${Math.round((totalCalls/totalContacts)*100)}% of total`}
          icon={<Phone className="w-6 h-6" />}
          color="green"
          onClick={() => onDrillDown('call_volume')}
        />
        <MetricCard
          title="SMS Sent"
          value={totalSMS.toString()}
          subtitle={`${Math.round((totalSMS/totalContacts)*100)}% of total`}
          icon={<MessageSquare className="w-6 h-6" />}
          color="purple"
          onClick={() => onDrillDown('call_volume')}
        />
        <MetricCard
          title="Emails Sent"
          value={totalEmails.toString()}
          subtitle={`${Math.round((totalEmails/totalContacts)*100)}% of total`}
          icon={<Mail className="w-6 h-6" />}
          color="orange"
          onClick={() => onDrillDown('call_volume')}
        />
      </div>

      {/* Channel Comparison Bar Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onDrillDown('call_volume')}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Communication Channels</h3>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
        <div className="space-y-6">
          {channelData.map((item) => (
            <div key={item.channel}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className={`text-${item.color}-600`}>{item.icon}</div>
                  <span className="font-medium text-gray-900">{item.channel}</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{item.count}</span>
              </div>
              <div className="relative w-full bg-gray-200 rounded-full h-8">
                <div
                  className={`absolute top-0 left-0 h-8 rounded-full bg-${item.color}-500 flex items-center justify-end pr-3 transition-all duration-500`}
                  style={{ width: `${(item.count / maxChannelCount) * 100}%` }}
                >
                  <span className="text-white text-sm font-semibold">
                    {Math.round((item.count / totalContacts) * 100)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Intent Breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onDrillDown('intent_breakdown')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Call Intent Distribution</h3>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
        <div className="space-y-3">
          {intentData.map((item) => (
            <div key={item.intent} className="flex items-center justify-between">
              <div className="flex items-center space-x-3 flex-1">
                <span className="font-medium text-gray-900">{item.intent}</span>
                <div className="flex-1 bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full bg-${item.color}-500`}
                    style={{ width: `${(item.count / totalCalls) * 100}%` }}
                  ></div>
                </div>
              </div>
              <span className="text-sm font-semibold text-gray-700 ml-4">{item.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Recommendations */}
      <AIRecommendations recommendations={aiRecommendations} />
    </div>
  );
}

function PatientsAnalytics({ 
  patients, 
  // filters, 
  onDrillDown }: any) {
  const totalPatients = patients.length;
  const newPatientsThisMonth = patients.filter((p: any) => {
    if (!p.createdAt) return false;
    const created = new Date(p.createdAt);
    const now = new Date();
    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
  }).length;

  const aiCreatedPatients = patients.filter((p: any) => p.aiCreated).length;
  const spanishSpeakers = patients.filter((p: any) => p.language === 'es').length;

  const aiRecommendations = [
    { insight: `${aiCreatedPatients} patients created by AI`, action: 'Review AI-created patient accuracy', priority: 'medium' },
    { insight: `${spanishSpeakers} Spanish-speaking patients`, action: 'Ensure adequate bilingual staff', priority: 'high' },
    { insight: 'Patient growth steady', action: 'Maintain current marketing efforts', priority: 'low' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Patients"
          value={totalPatients.toString()}
          subtitle="Active patients"
          icon={<Users className="w-6 h-6" />}
          color="blue"
          onClick={() => onDrillDown('patient_growth')}
        />
        <MetricCard
          title="New This Month"
          value={newPatientsThisMonth.toString()}
          subtitle="Month to date"
          icon={<TrendingUp className="w-6 h-6" />}
          color="green"
          onClick={() => onDrillDown('patient_growth')}
        />
        <MetricCard
          title="AI Created"
          value={aiCreatedPatients.toString()}
          subtitle={`${Math.round((aiCreatedPatients/totalPatients)*100)}% AI-generated`}
          icon={<Brain className="w-6 h-6" />}
          color="purple"
          onClick={() => onDrillDown('patient_demographics')}
        />
        <MetricCard
          title="Spanish Speakers"
          value={spanishSpeakers.toString()}
          subtitle={`${Math.round((spanishSpeakers/totalPatients)*100)}% of total`}
          icon={<MessageSquare className="w-6 h-6" />}
          color="orange"
          onClick={() => onDrillDown('patient_demographics')}
        />
      </div>

      {/* Demographics Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onDrillDown('patient_demographics')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Patient Demographics</h3>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-3xl font-bold text-blue-600">{Math.round((spanishSpeakers/totalPatients)*100)}%</p>
            <p className="text-sm text-gray-600">Spanish Preferred</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-3xl font-bold text-green-600">{Math.round(((totalPatients-spanishSpeakers)/totalPatients)*100)}%</p>
            <p className="text-sm text-gray-600">English Preferred</p>
          </div>
        </div>
      </div>

      <AIRecommendations recommendations={aiRecommendations} />
    </div>
  );
}

function RemindersAnalytics({ 
  reminders, 
  // filters, 
  onDrillDown }: any) {
  const totalReminders = reminders.length;
  const sent = reminders.filter((r: any) => r.status === 'sent' || r.status === 'delivered').length;
  const scheduled = reminders.filter((r: any) => r.status === 'scheduled').length;
  const failed = reminders.filter((r: any) => r.status === 'failed').length;

  const deliveryRate = totalReminders > 0 ? Math.round((sent / totalReminders) * 100) : 0;

  const aiRecommendations = [
    { insight: `${deliveryRate}% successful delivery rate`, action: deliveryRate < 95 ? 'Investigate delivery failures' : 'Maintain current practices', priority: deliveryRate < 95 ? 'high' : 'low' },
    { insight: `${scheduled} reminders scheduled`, action: 'Monitor upcoming reminder queue', priority: 'medium' },
    { insight: 'SMS shows better engagement', action: 'Prioritize SMS for time-sensitive reminders', priority: 'medium' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Reminders"
          value={totalReminders.toString()}
          subtitle="All time"
          icon={<Bell className="w-6 h-6" />}
          color="blue"
          onClick={() => onDrillDown('reminder_delivery')}
        />
        <MetricCard
          title="Delivered"
          value={sent.toString()}
          subtitle={`${deliveryRate}% success rate`}
          icon={<CheckCircle className="w-6 h-6" />}
          color="green"
          onClick={() => onDrillDown('reminder_delivery')}
        />
        <MetricCard
          title="Scheduled"
          value={scheduled.toString()}
          subtitle="Pending delivery"
          icon={<Clock className="w-6 h-6" />}
          color="yellow"
          onClick={() => onDrillDown('reminder_delivery')}
        />
        <MetricCard
          title="Failed"
          value={failed.toString()}
          subtitle="Requires attention"
          icon={<XCircle className="w-6 h-6" />}
          color="red"
          onClick={() => onDrillDown('reminder_delivery')}
        />
      </div>

      {/* Channel Performance */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onDrillDown('reminder_effectiveness')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Delivery Channel Performance</h3>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <p className="text-2xl font-bold text-blue-600">96%</p>
            <p className="text-sm text-gray-600">SMS Success</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <Mail className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <p className="text-2xl font-bold text-green-600">89%</p>
            <p className="text-sm text-gray-600">Email Success</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <Phone className="w-8 h-8 mx-auto mb-2 text-purple-600" />
            <p className="text-2xl font-bold text-purple-600">92%</p>
            <p className="text-sm text-gray-600">Call Success</p>
          </div>
        </div>
      </div>

      <AIRecommendations recommendations={aiRecommendations} />
    </div>
  );
}

function AppointmentsAnalytics({ 
  appointments, 
  // filters, 
  onDrillDown }: any) {
  const totalAppointments = appointments.length;
  const scheduled = appointments.filter((a: any) => a.status === 'scheduled' || a.status === 'confirmed').length;
  const completed = appointments.filter((a: any) => a.status === 'completed').length;
  const noShows = appointments.filter((a: any) => a.status === 'no-show').length;
  const aiBooked = appointments.filter((a: any) => a.aiBooked).length;

  const completionRate = totalAppointments > 0 ? Math.round((completed / totalAppointments) * 100) : 0;
  const noShowRate = totalAppointments > 0 ? Math.round((noShows / totalAppointments) * 100) : 0;

  const aiRecommendations = [
    { insight: `${noShowRate}% no-show rate`, action: noShowRate > 10 ? 'Implement reminder system improvements' : 'Maintain current reminder strategy', priority: noShowRate > 10 ? 'high' : 'low' },
    { insight: `${aiBooked} appointments booked by AI`, action: 'AI booking performing well', priority: 'low' },
    { insight: `${completionRate}% completion rate`, action: 'Track reasons for cancellations', priority: 'medium' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Appointments"
          value={totalAppointments.toString()}
          subtitle="All time"
          icon={<Calendar className="w-6 h-6" />}
          color="blue"
          onClick={() => onDrillDown('appointment_status')}
        />
        <MetricCard
          title="Upcoming"
          value={scheduled.toString()}
          subtitle="Scheduled/Confirmed"
          icon={<Clock className="w-6 h-6" />}
          color="yellow"
          onClick={() => onDrillDown('appointment_status')}
        />
        <MetricCard
          title="Completed"
          value={completed.toString()}
          subtitle={`${completionRate}% completion`}
          icon={<CheckCircle className="w-6 h-6" />}
          color="green"
          onClick={() => onDrillDown('appointment_status')}
        />
        <MetricCard
          title="No-Shows"
          value={noShows.toString()}
          subtitle={`${noShowRate}% rate`}
          icon={<XCircle className="w-6 h-6" />}
          color="red"
          onClick={() => onDrillDown('appointment_status')}
        />
      </div>

      {/* Appointment Types */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onDrillDown('appointment_types')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Appointment Types Distribution</h3>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['cleaning', 'consultation', 'emergency', 'procedure', 'followup'].map((type) => {
            const count = appointments.filter((a: any) => a.type === type).length;
            const percentage = totalAppointments > 0 ? Math.round((count / totalAppointments) * 100) : 0;
            return (
              <div key={type} className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <p className="text-xs text-gray-600 capitalize">{type}</p>
                <p className="text-xs text-gray-500">{percentage}%</p>
              </div>
            );
          })}
        </div>
      </div>

      <AIRecommendations recommendations={aiRecommendations} />
    </div>
  );
}

function EducationAnalytics({ 
  // filters, 
  onDrillDown }: any) {
  const aiRecommendations = [
    { insight: 'Video content shows 3x engagement', action: 'Prioritize video content creation', priority: 'high' },
    { insight: 'Treatment acceptance up 24% with education', action: 'Expand educational content library', priority: 'high' },
    { insight: 'Spanish content underutilized', action: 'Create more Spanish educational materials', priority: 'medium' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Content Library"
          value="47"
          subtitle="Active resources"
          icon={<BookOpen className="w-6 h-6" />}
          color="blue"
          onClick={() => onDrillDown('content_performance')}
        />
        <MetricCard
          title="Delivered"
          value="234"
          subtitle="This month"
          icon={<CheckCircle className="w-6 h-6" />}
          color="green"
          onClick={() => onDrillDown('education_engagement')}
        />
        <MetricCard
          title="Engagement Rate"
          value="78%"
          subtitle="Viewed/Completed"
          icon={<Eye className="w-6 h-6" />}
          color="purple"
          onClick={() => onDrillDown('education_engagement')}
        />
        <MetricCard
          title="Avg Watch Time"
          value="4.2min"
          subtitle="Per video"
          icon={<Video className="w-6 h-6" />}
          color="orange"
          onClick={() => onDrillDown('content_performance')}
        />
      </div>

      {/* Top Performing Content */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onDrillDown('content_performance')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Top Performing Content</h3>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
        <div className="space-y-3">
          {[
            { title: 'Root Canal Procedure Guide', views: 87, engagement: 92 },
            { title: 'Dental Implant Overview', views: 76, engagement: 88 },
            { title: 'Teeth Whitening Options', views: 65, engagement: 85 }
          ].map((content, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{content.title}</p>
                <p className="text-sm text-gray-600">{content.views} views • {content.engagement}% engagement</p>
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
          ))}
        </div>
      </div>

      <AIRecommendations recommendations={aiRecommendations} />
    </div>
  );
}

function NewUsersAnalytics({ 
  patients, 
  // filters, 
  onDrillDown }: any) {
  const newThisWeek = patients.filter((p: any) => {
    if (!p.createdAt) return false;
    const created = new Date(p.createdAt);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return created >= weekAgo;
  }).length;

  const newThisMonth = patients.filter((p: any) => {
    if (!p.createdAt) return false;
    const created = new Date(p.createdAt);
    const now = new Date();
    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
  }).length;

  const aiRecommendations = [
    { insight: 'User growth trending up', action: 'Maintain current acquisition strategy', priority: 'low' },
    { insight: 'AI creating accurate patient profiles', action: 'Monitor AI accuracy metrics', priority: 'medium' },
    { insight: 'Onboarding completion at 94%', action: 'Excellent onboarding performance', priority: 'low' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="New This Week"
          value={newThisWeek.toString()}
          subtitle="Last 7 days"
          icon={<Users className="w-6 h-6" />}
          color="blue"
          onClick={() => onDrillDown('patient_growth')}
        />
        <MetricCard
          title="New This Month"
          value={newThisMonth.toString()}
          subtitle="Month to date"
          icon={<TrendingUp className="w-6 h-6" />}
          color="green"
          onClick={() => onDrillDown('patient_growth')}
        />
        <MetricCard
          title="Growth Rate"
          value="+12%"
          subtitle="vs last month"
          icon={<Activity className="w-6 h-6" />}
          color="purple"
          onClick={() => onDrillDown('patient_growth')}
        />
        <MetricCard
          title="Onboarding Rate"
          value="94%"
          subtitle="Completion"
          icon={<Target className="w-6 h-6" />}
          color="orange"
          onClick={() => onDrillDown('patient_growth')}
        />
      </div>

      {/* Growth Trend */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => onDrillDown('patient_growth')}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Monthly Growth Trend</h3>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
        <div className="flex items-end space-x-2 h-40">
          {[12, 15, 18, 14, 22, 19, 25].map((value, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center justify-end">
              <div
                className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                style={{ height: `${(value / 25) * 100}%` }}
              ></div>
              <span className="text-xs text-gray-600 mt-2">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <AIRecommendations recommendations={aiRecommendations} />
    </div>
  );
}

function MetricCard({ title, value, subtitle, icon, color, onClick }: any) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600'
  };

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all cursor-pointer text-left group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-lg ${colorClasses[color as keyof typeof colorClasses]}`}>
          {icon}
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
      </div>
      <p className="text-sm text-gray-600 mb-1">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </button>
  );
}

function AIRecommendations({ recommendations }: any) {
  return (
    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border-2 border-amber-200 p-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className="bg-amber-200 rounded-full p-2">
          <Brain className="w-6 h-6 text-amber-700" />
        </div>
        <h3 className="text-lg font-bold text-amber-900">AI-Powered Recommendations</h3>
      </div>
      <div className="space-y-3">
        {recommendations.map((rec: any, idx: number) => (
          <div key={idx} className="bg-white rounded-lg p-4 border border-amber-200">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Lightbulb className="w-5 h-5 text-amber-600" />
                <p className="font-semibold text-amber-900">{rec.insight}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'
              }`}>
                {rec.priority}
              </span>
            </div>
            <p className="text-sm text-amber-800 ml-7">{rec.action}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DrillDownModal({ 
  type, onClose, calls, patients, 
  // reminders, appointments 
}: any) {
  const renderContent = () => {
    switch (type) {
      case 'call_volume':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Call Volume Details</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-600">Total Calls</p>
                <p className="text-3xl font-bold text-blue-600">{calls.length}</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p className="text-sm text-gray-600">Inbound</p>
                <p className="text-3xl font-bold text-green-600">{calls.filter((c: any) => c.type === 'inbound').length}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded">
                <p className="text-sm text-gray-600">Outbound</p>
                <p className="text-3xl font-bold text-purple-600">{calls.filter((c: any) => c.type === 'outbound').length}</p>
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">Hourly Breakdown</h4>
              {[9, 10, 11, 12, 13, 14, 15, 16].map(hour => (
                <div key={hour} className="flex items-center space-x-3">
                  <span className="w-16 text-sm">{hour}:00</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-6">
                    <div className="bg-blue-500 h-6 rounded-full" style={{ width: `${Math.random() * 100}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'intent_breakdown':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Intent Recognition Breakdown</h3>
            <div className="space-y-3">
              {['emergency', 'appointment', 'follow-up', 'inquiry', 'reminder'].map(intent => {
                const count = calls.filter((c: any) => c.purpose === intent).length;
                return (
                  <div key={intent} className="bg-gray-50 p-4 rounded">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium capitalize">{intent}</span>
                      <span className="font-bold">{count} calls</span>
                    </div>
                    <div className="text-sm text-gray-600">Avg confidence: 94%</div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 'patient_demographics':
        return (
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Patient Demographics</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded">
                <p className="text-sm text-gray-600">English Speakers</p>
                <p className="text-3xl font-bold text-blue-600">{patients.filter((p: any) => p.language === 'en').length}</p>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <p className="text-sm text-gray-600">Spanish Speakers</p>
                <p className="text-3xl font-bold text-green-600">{patients.filter((p: any) => p.language === 'es').length}</p>
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded">
              <p className="text-sm text-gray-600 mb-2">AI-Created Patients</p>
              <p className="text-3xl font-bold text-purple-600">{patients.filter((p: any) => p.aiCreated).length}</p>
              <p className="text-sm text-purple-700 mt-1">Demonstrates AI conversation quality</p>
            </div>
          </div>
        );
      default:
        return <div className="text-center py-8 text-gray-500">Detailed analytics coming soon</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Detailed Analytics</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
